/**
 * Initializes footer sections by collapsing/expanding based on screen size,
 * setting appropriate ARIA attributes, and attaching resize listeners.
 */
export const runFooter = () => {
    const footerSections = document.querySelectorAll('.footer-section-content > details');
    if (!footerSections.length) return;

    /**
     * Class representing a footer section and its behavior.
     */
    class Footer {
        /**
         * @param {HTMLElement} element - The footer `<details>` element.
         */
        constructor(element) {
            /** @type {HTMLElement} */
            this.footer = element;

            /** @type {HTMLElement | null} */
            this.summary = this.footer.querySelector('summary');

            this.init();
        }

        /**
         * Initializes the footer: attaches the one-time keyboard/click guards,
         * sets the initial open/collapsed state, and starts listening for resize.
         */
        init() {
            this.attachSummaryGuards();
            this.updateState();
            this.addResizeListener();
        }

        /**
         * Sets the open or collapsed state of the footer based on screen size.
         * On mobile, the section is collapsed and the summary is a normal,
         * tabbable toggle. On desktop, the section is forced open and the
         * summary is pulled out of the tab order, since there's nothing to
         * toggle there.
         */
        updateState() {
            if (isMobile()) {
                this.footer.open = false;
                //this.summary.removeAttribute('tabindex');
            } else {
                this.footer.open = true;
                //this.summary.setAttribute('tabindex', '-1');
            }
        }

        /**
         * Attaches keydown and click guards to the summary, once per instance.
         *
         * These only take effect on desktop (checked at event time via
         * isMobile(), not at attach time) so that resizing the window doesn't
         * require adding/removing listeners and can't stack duplicates.
         *
         * On desktop the summary is meant to stay permanently open and out of
         * the tab order, so both keyboard activation (Enter/Space) and mouse
         * clicks are prevented from toggling it closed. On mobile, nothing is
         * blocked and the summary behaves like a normal disclosure control.
         */
        attachSummaryGuards() {
            if (!this.summary) return;

            // Block Enter/Space from collapsing the summary on desktop.
            this.summary.addEventListener('keydown', (event) => {
                if (isMobile()) return;
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                }
            });

            // Block mouse clicks from collapsing the summary on desktop.
            this.summary.addEventListener('click', (event) => {
                if (isMobile()) return;
                event.preventDefault();
            });
        }

        /**
         * Attaches a throttled resize event listener to update footer state.
         */
        addResizeListener() {
            window.addEventListener('resize', throttle(() => this.updateState(), 100));
        }
    }

    footerSections.forEach(section => new Footer(section));
};

/**
 * Updates the CSS variable `--ft` to the current height of the footer.
 * This is used to offset main content in parallax layouts.
 */
export const getFooterHeight = () => {
    const footer = document.getElementById('page-footer');
    if (!footer || isMobile()) return;

    const root = document.documentElement;
    root.style.removeProperty('--ft');

    const height = footer.getBoundingClientRect().height;
    root.style.setProperty('--ft', `${Math.round(height)}px`);
};

/**
 * Attaches click behavior to a "Skip to footer" link that scrolls to the bottom
 * of the page, useful for accessibility and overlays.
 */
export const scrollToBottom = () => {
    const skipFooterBtn = document.getElementById('skip-footer');
    if (!skipFooterBtn) return;

    skipFooterBtn.addEventListener('click', () => {
        const scrollingElement = document.scrollingElement || document.body;
        scrollingElement.scrollTop = scrollingElement.scrollHeight;
    });
};